import React, { useEffect, useRef, useState } from "react";
import Iconify from "../../layouts/components/iconify/iconify";
import Avatar from "../ui/avatar/Avtar";
import "./TaskGridBulkBar.scss";

const PANEL = {
  NONE: null,
  TYPE: "type",
  STATUS: "status",
  PRIORITY: "priority",
  ASSIGNEE: "assignee",
  ESTIMATE: "estimate",
  DEADLINE: "deadline",
  MORE: "more",
};

const TaskGridBulkBar = ({
  selectedCount,
  isBulkApplying = false,
  allowEditStatus = true,
  allowEditAssignee = false,
  taskTypeList = [],
  statusList = [],
  priorityList = [],
  userList = [],
  disabled = false,
  onClearSelection,
  onTypeSelect,
  onStatusSelect,
  onPrioritySelect,
  onAssigneeSelect,
  onEstimateApply,
  onDeadlineApply,
}) => {
  const [openPanel, setOpenPanel] = useState(PANEL.NONE);
  const [estimateHours, setEstimateHours] = useState("");
  const [deadline, setDeadline] = useState("");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const barRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (barRef.current && !barRef.current.contains(event.target)) {
        setOpenPanel(PANEL.NONE);
        setAssigneeSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (disabled || isBulkApplying) {
      setOpenPanel(PANEL.NONE);
      setAssigneeSearch("");
    }
  }, [disabled, isBulkApplying]);

  const togglePanel = (panel) => {
    if (disabled || isBulkApplying) return;
    setOpenPanel((current) => (current === panel ? PANEL.NONE : panel));
  };

  const closePanel = () => {
    setOpenPanel(PANEL.NONE);
    setAssigneeSearch("");
  };

  const filteredUserList = (userList || []).filter((user) =>
    (user.label || "").toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const handleOptionSelect = (callback, option) => {
    if (!option || disabled) return;
    callback?.(option);
    closePanel();
  };

  const handleEstimateApply = () => {
    onEstimateApply?.(estimateHours);
    closePanel();
  };

  const handleDeadlineApply = () => {
    onDeadlineApply?.(deadline);
    closePanel();
  };

  const openFieldFromMore = (panel) => {
    setOpenPanel(panel);
  };

  return (
    <div
      className={`task-grid-bulk-bar ${isBulkApplying ? "is-applying" : ""}`}
      role="status"
      aria-live="polite"
      ref={barRef}
    >
      <div className="task-grid-bulk-bar__zone task-grid-bulk-bar__zone--selection">
        <span className="task-grid-bulk-bar__count-pill">
          <Iconify icon="mdi:checkbox-marked-circle-outline" width={16} />
          <span className="task-grid-bulk-bar__count-number">{selectedCount}</span>
          selected
        </span>
        <button
          type="button"
          className="task-grid-bulk-bar__clear"
          disabled={isBulkApplying || selectedCount === 0}
          onClick={onClearSelection}
        >
          Clear
        </button>
        {isBulkApplying && (
          <span className="task-grid-bulk-bar__applying">
            <span className="task-grid-bulk-bar__spinner" aria-hidden="true" />
            Updating…
          </span>
        )}
      </div>

      <div className="task-grid-bulk-bar__zone task-grid-bulk-bar__zone--actions">
        <span className="task-grid-bulk-bar__label">Bulk update:</span>

        <div className="task-grid-bulk-bar__chip-wrap">
          <button
            type="button"
            className={`task-grid-bulk-bar__chip ${openPanel === PANEL.TYPE ? "is-open" : ""}`}
            disabled={disabled}
            onClick={() => togglePanel(PANEL.TYPE)}
            aria-expanded={openPanel === PANEL.TYPE}
          >
            Type
            <Iconify icon="mdi:chevron-down" width={16} />
          </button>
          {openPanel === PANEL.TYPE && (
            <div className="task-grid-bulk-bar__menu" role="menu">
              {(taskTypeList || []).map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className="task-grid-bulk-bar__menu-item"
                  role="menuitem"
                  onClick={() => handleOptionSelect(onTypeSelect, item)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {allowEditStatus && (
          <div className="task-grid-bulk-bar__chip-wrap">
            <button
              type="button"
              className={`task-grid-bulk-bar__chip ${openPanel === PANEL.STATUS ? "is-open" : ""}`}
              disabled={disabled}
              onClick={() => togglePanel(PANEL.STATUS)}
              aria-expanded={openPanel === PANEL.STATUS}
            >
              Status
              <Iconify icon="mdi:chevron-down" width={16} />
            </button>
            {openPanel === PANEL.STATUS && (
              <div className="task-grid-bulk-bar__menu" role="menu">
                {(statusList || []).map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    className="task-grid-bulk-bar__menu-item"
                    role="menuitem"
                    onClick={() => handleOptionSelect(onStatusSelect, item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="task-grid-bulk-bar__chip-wrap">
          <button
            type="button"
            className={`task-grid-bulk-bar__chip ${openPanel === PANEL.PRIORITY ? "is-open" : ""}`}
            disabled={disabled}
            onClick={() => togglePanel(PANEL.PRIORITY)}
            aria-expanded={openPanel === PANEL.PRIORITY}
          >
            Priority
            <Iconify icon="mdi:chevron-down" width={16} />
          </button>
          {openPanel === PANEL.PRIORITY && (
            <div className="task-grid-bulk-bar__menu" role="menu">
              {(priorityList || []).map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className="task-grid-bulk-bar__menu-item"
                  role="menuitem"
                  onClick={() => handleOptionSelect(onPrioritySelect, item)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {allowEditAssignee && (
          <div className="task-grid-bulk-bar__chip-wrap">
            <button
              type="button"
              className={`task-grid-bulk-bar__chip ${openPanel === PANEL.ASSIGNEE ? "is-open" : ""}`}
              disabled={disabled}
              onClick={() => togglePanel(PANEL.ASSIGNEE)}
              aria-expanded={openPanel === PANEL.ASSIGNEE}
            >
              Assignee
              <Iconify icon="mdi:chevron-down" width={16} />
            </button>
            {openPanel === PANEL.ASSIGNEE && (
              <div className="task-grid-bulk-bar__assignee-panel" role="dialog" aria-label="Bulk assignee">
                <input
                  type="text"
                  className="task-grid-bulk-bar__input task-grid-bulk-bar__assignee-search"
                  placeholder="Search Assignee..."
                  value={assigneeSearch}
                  disabled={disabled}
                  onChange={(e) => setAssigneeSearch(e.target.value)}
                />
                <div className="task-grid-bulk-bar__assignee-list">
                  {filteredUserList.length > 0 ? (
                    filteredUserList.map((user) => (
                      <button
                        key={user.value}
                        type="button"
                        className="task-grid-bulk-bar__assignee-item"
                        onClick={() => handleOptionSelect(onAssigneeSelect, user)}
                      >
                        <Avatar
                          src={user.avatar}
                          alt={user.label}
                          size="small"
                          shape="circle"
                          tooltip={false}
                        />
                        <span className="task-grid-bulk-bar__assignee-name">{user.label}</span>
                      </button>
                    ))
                  ) : (
                    <div className="task-grid-bulk-bar__assignee-empty">No record found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="task-grid-bulk-bar__chip-wrap task-grid-bulk-bar__chip-wrap--desktop">
          <button
            type="button"
            className={`task-grid-bulk-bar__chip ${openPanel === PANEL.ESTIMATE ? "is-open" : ""}`}
            disabled={disabled}
            onClick={() => togglePanel(PANEL.ESTIMATE)}
            aria-expanded={openPanel === PANEL.ESTIMATE}
          >
            Est.
            <Iconify icon="mdi:chevron-down" width={16} />
          </button>
          {openPanel === PANEL.ESTIMATE && (
            <div className="task-grid-bulk-bar__popover" role="dialog" aria-label="Bulk estimate">
              <label className="task-grid-bulk-bar__popover-label" htmlFor="bulk-est-hours">
                Estimate hours
              </label>
              <div className="task-grid-bulk-bar__popover-row">
                <input
                  id="bulk-est-hours"
                  type="number"
                  min="0"
                  step="0.25"
                  className="task-grid-bulk-bar__input"
                  placeholder="0"
                  value={estimateHours}
                  disabled={disabled}
                  onChange={(e) => setEstimateHours(e.target.value)}
                />
                <button
                  type="button"
                  className="task-grid-bulk-bar__apply"
                  disabled={disabled}
                  onClick={handleEstimateApply}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="task-grid-bulk-bar__chip-wrap task-grid-bulk-bar__chip-wrap--desktop">
          <button
            type="button"
            className={`task-grid-bulk-bar__chip ${openPanel === PANEL.DEADLINE ? "is-open" : ""}`}
            disabled={disabled}
            onClick={() => togglePanel(PANEL.DEADLINE)}
            aria-expanded={openPanel === PANEL.DEADLINE}
          >
            Deadline
            <Iconify icon="mdi:chevron-down" width={16} />
          </button>
          {openPanel === PANEL.DEADLINE && (
            <div className="task-grid-bulk-bar__popover" role="dialog" aria-label="Bulk deadline">
              <label className="task-grid-bulk-bar__popover-label" htmlFor="bulk-deadline">
                Deadline
              </label>
              <div className="task-grid-bulk-bar__popover-row">
                <input
                  id="bulk-deadline"
                  type="date"
                  className="task-grid-bulk-bar__input"
                  value={deadline}
                  disabled={disabled}
                  onChange={(e) => setDeadline(e.target.value)}
                />
                <button
                  type="button"
                  className="task-grid-bulk-bar__apply"
                  disabled={disabled}
                  onClick={handleDeadlineApply}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="task-grid-bulk-bar__chip-wrap task-grid-bulk-bar__chip-wrap--more">
          <button
            type="button"
            className={`task-grid-bulk-bar__chip ${openPanel === PANEL.MORE || openPanel === PANEL.ESTIMATE || openPanel === PANEL.DEADLINE ? "is-open" : ""}`}
            disabled={disabled}
            onClick={() => togglePanel(PANEL.MORE)}
            aria-expanded={openPanel === PANEL.MORE}
          >
            More
            <Iconify icon="mdi:chevron-down" width={16} />
          </button>
          {openPanel === PANEL.MORE && (
            <div className="task-grid-bulk-bar__menu" role="menu">
              <button
                type="button"
                className="task-grid-bulk-bar__menu-item"
                role="menuitem"
                onClick={() => openFieldFromMore(PANEL.ESTIMATE)}
              >
                Estimate hours
              </button>
              <button
                type="button"
                className="task-grid-bulk-bar__menu-item"
                role="menuitem"
                onClick={() => openFieldFromMore(PANEL.DEADLINE)}
              >
                Deadline
              </button>
            </div>
          )}
          {openPanel === PANEL.ESTIMATE && (
            <div className="task-grid-bulk-bar__popover task-grid-bulk-bar__popover--from-more" role="dialog" aria-label="Bulk estimate">
              <label className="task-grid-bulk-bar__popover-label" htmlFor="bulk-est-hours-more">
                Estimate hours
              </label>
              <div className="task-grid-bulk-bar__popover-row">
                <input
                  id="bulk-est-hours-more"
                  type="number"
                  min="0"
                  step="0.25"
                  className="task-grid-bulk-bar__input"
                  placeholder="0"
                  value={estimateHours}
                  disabled={disabled}
                  onChange={(e) => setEstimateHours(e.target.value)}
                />
                <button
                  type="button"
                  className="task-grid-bulk-bar__apply"
                  disabled={disabled}
                  onClick={handleEstimateApply}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
          {openPanel === PANEL.DEADLINE && (
            <div className="task-grid-bulk-bar__popover task-grid-bulk-bar__popover--from-more" role="dialog" aria-label="Bulk deadline">
              <label className="task-grid-bulk-bar__popover-label" htmlFor="bulk-deadline-more">
                Deadline
              </label>
              <div className="task-grid-bulk-bar__popover-row">
                <input
                  id="bulk-deadline-more"
                  type="date"
                  className="task-grid-bulk-bar__input"
                  value={deadline}
                  disabled={disabled}
                  onChange={(e) => setDeadline(e.target.value)}
                />
                <button
                  type="button"
                  className="task-grid-bulk-bar__apply"
                  disabled={disabled}
                  onClick={handleDeadlineApply}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskGridBulkBar;
